import MouseMode from "../../settings/MouseMode";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import {ViewerMode} from "../../ViewerMode";
import {TextSearchGlobals} from "../../../document/components/pdf/TextSearch";
import LinkService from "../../LinkService";
import DownloadManager from "../../DownloadManager";
import React, {createRef, ReactNode, RefObject} from "react";
import DomHelper from "../../../../shared/helper/DomHelper";
import {pdfjs} from "react-pdf";
import CssMatrix from "../../../../shared/interface/CssMatrix";
import {
	Annotation,
	AnnotationPoint,
	AnnotationRectangle as MetadataAnnotationRectangle,
	AnnotationsDocument,
	MetadataRectangle,
	PagePages,
	Positions
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import AnnotationRectangle from "./AnnotationRectangle";
import AnnotationFactory from "./AnnotationFactory";
import DocumentSettings from "../../../document/DocumentSettings";
import {AnnotationSubType} from "./AnnotationSubType";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import Observer from "../../../../shared/class/Observer";
import LocationEvent from "../../LocationEvent";
import DestinationEvent from "../../DestinationEvent";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {PortalAnnotation} from "../../../../shared/models/PortalAnnotation";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

interface AnnotationLayerProperties extends TextSearchGlobals, I18nGlobals, DebugGlobals {
	pageNumber: number;
	currentPage: number;
	zoomValue: number;
	numberOfPages: number;
	context: PortalContext;
	mouseMode: MouseMode;
	viewerMode: ViewerMode;
	locationObserver?: Observer<LocationEvent>;
	destinationObserver?: Observer<DestinationEvent>;
	portalDocument: Immutable<PortalDocumentInterface>;
}

interface AnnotationLayerStatus {
	annotations: Array<ReactNode>;
	annotationMarker: Array<ReactNode>;
	signatures: Array<ReactNode>;
}

export class AnnotationLayerDefinition extends PortalPureComponent<AnnotationLayerProperties, AnnotationLayerStatus> {
	private annotationLayer: RefObject<HTMLDivElement>;
	private annotationLayerGenerated: RefObject<HTMLDivElement>;
	private readonly knownAnnotations: Array<AnnotationSubType> = [
		AnnotationSubType.HIGHLIGHT,
		AnnotationSubType.UNDERLINE,
		AnnotationSubType.STRIKEOUT,
		AnnotationSubType.FREETEXT,
		AnnotationSubType.SQUARE,
		AnnotationSubType.CIRCLE,
		AnnotationSubType.SQUIGGLY,
		// AnnotationSubType.WIDGET,
		AnnotationSubType.STAMP,
		AnnotationSubType.FILEATTACHMENT,
		AnnotationSubType.TEXT,
		AnnotationSubType.LINE,
		AnnotationSubType.POLYLINE,
		AnnotationSubType.POLYGON,
		AnnotationSubType.INK,
		AnnotationSubType.REDACT,
		// AnnotationSubType.LINK
	];

	/**
	 * pdf page constructor
	 */
	constructor(props: Readonly<AnnotationLayerProperties>) {
		super(props);

		this.annotationLayer = createRef<HTMLDivElement>();
		this.annotationLayerGenerated = createRef<HTMLDivElement>();

		this.generateAnnotations = FunctionHelper.defer(this.generateAnnotations.bind(this));
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.generateAnnotations();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<AnnotationLayerProperties>, prevState: Readonly<AnnotationLayerStatus>
	): Promise<void> {
		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument.get().id,
			this.props.portalDocument.get().updated !== prevProps.portalDocument.get().updated,
			this.props.zoomValue !== prevProps.zoomValue
		];

		if (documentChanged.includes(true)) {
			await this.generateAnnotations();
		}
	}

	private getTextBoundaries(textElement: any): MetadataRectangle {
		let result: MetadataRectangle = MetadataRectangle.fromJson({
			width: 0,
			height: 0,
			x: 0,
			y: 0
		} as MetadataRectangle);

		if (FunctionHelper.exists(textElement.data)) {
			let matrix: Array<number> = FunctionHelper.applyValue(
				textElement.data?.transform, [0, 0, 0, 0, 0, 0]
			);
			let rotation = Math.atan2(matrix[1], matrix[0]);

			let xPos: number = UnitHelper.unitToPx(matrix[4], LengthUnit.POINTS, this.props.portalResolution);
			let yPos: number = UnitHelper.unitToPx(matrix[5], LengthUnit.POINTS, this.props.portalResolution);
			let width: number = UnitHelper.unitToPx(
				FunctionHelper.applyValue(textElement.data.width, 0), LengthUnit.POINTS,
				this.props.portalResolution
			);
			let height: number = UnitHelper.unitToPx(
				Math.sqrt(matrix[2] * matrix[2] + matrix[3] * matrix[3]), LengthUnit.POINTS,
				this.props.portalResolution
			);

			result = MetadataRectangle.fromJson({
				x: xPos,
				y: yPos,
				height: height,
				width: width
			} as MetadataRectangle);

			if (rotation !== 0) {
				result.x! += height * Math.sin(rotation);
				result.y! -= height * Math.cos(rotation);
			}
		}

		return result;
	}

	/**
	 * Render PDF annotations
	 */
	private async generateAnnotations(): Promise<void> {
		if (!FunctionHelper.exists(this.annotationLayer.current) ||
			!FunctionHelper.exists(this.annotationLayerGenerated.current)) {
			return;
		}

		// clear annotation layer
		DomHelper.html(this.annotationLayerGenerated.current!, "");

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);
			let documentProxy: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let pageProxy: pdfjs.PDFPageProxy = await documentProxy.getPage(this.props.pageNumber);
			let scaledViewport: pdfjs.PageViewport = pageProxy.getViewport({
				// pdfjs is using points so scale must be adapted to this
				scale: UnitHelper.unitToPx(
					this.props.zoomValue / 100, LengthUnit.POINTS, this.props.portalResolution
				),
				dontFlip: true
			});

			let generatedAnnotations: Array<any> = await pageProxy.getAnnotations({intent: "display"});

			// Render all annotation with apperance stream via pdfjs
			let annotationsWithAppearance: Array<any> = [];
			for (let annotation of generatedAnnotations) {
				if (FunctionHelper.exists(annotation.inReplyTo) || annotation.subtype === AnnotationSubType.WIDGET ||
					!(annotation.hasAppearance || annotation.annotationType === 2)) {
					continue;
				}

				annotationsWithAppearance.push(annotation);
			}

			pdfjs.AnnotationLayer.render({
				div: this.annotationLayerGenerated.current!,
				annotations: annotationsWithAppearance,
				viewport: scaledViewport,
				page: pageProxy,
				renderForms: false,
				imageResourcesPath: "image/annotation/",
				linkService: new LinkService({...this.props}),
				downloadManager: new DownloadManager()
			});

			let linkAnnotations: Array<HTMLElement> = DomHelper.findAll(
				this.annotationLayerGenerated.current!, ".linkAnnotation a"
			);
			for (let linkAnnotation of linkAnnotations) {
				linkAnnotation.setAttribute("target", "_blank");
			}

			// fix popup rotation and scale
			let popups: Array<HTMLElement> = DomHelper.findAll(
				this.annotationLayerGenerated.current!, ".popupWrapper"
			);
			for (let popup of popups) {
				let popupDate: HTMLElement | null = DomHelper.find(popup, ".popupDate");
				if (FunctionHelper.exists(popupDate)) {
					let popupData: any = JSON.parse(
						FunctionHelper.applyValue(popupDate!.getAttribute("data-l10n-args"), "{}")
					);

					let popupDateContent: string = popupDate!.innerHTML;
					popupDateContent = popupDateContent.replaceAll("{{date}}", popupData.date);
					popupDateContent = popupDateContent.replaceAll("{{time}}", popupData.time);
					DomHelper.html(popupDate!, popupDateContent);
				}

				let cssMatrix: CssMatrix | null = DomHelper.decomposeCssMatrix(
					FunctionHelper.applyValue(popup.parentElement?.style.transform, "")
				);
				if (cssMatrix === null) {
					continue;
				}

				let x: number = parseFloat(popup.style.left);
				switch (Math.floor(cssMatrix.angle)) {
					case 180:
						popup.style.left = "0";
						popup.style.top = x + "px";
						break;
					case 90:
						popup.style.left = "0";
						break;
					case -90:
						popup.style.top = x + "px";
						break;
				}

				popup.style.transform = "rotate(" + (-cssMatrix.angle) + "deg) scale(" + (1 / cssMatrix.scaleX) + ")";
				popup.style.transformOrigin = "0 0";
			}

			// remove image alt tags
			let imgElements: Array<HTMLElement> = DomHelper.findAll(
				this.annotationLayerGenerated.current!, "img"
			);
			for (let imgElement of imgElements) {
				imgElement.setAttribute("alt", "");
			}
		} catch (e: any) {
			// can be ignored here
		}
	}

	private renderAnnotations(): Array<ReactNode> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let annotations: Array<ReactNode> = [];

		for (let annotation of FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		)) {
			if (FunctionHelper.exists(annotation.appearance) && annotation.subType !== AnnotationSubType.REDACT) {
				continue;
			}

			if (!annotation.viewable || annotation.hidden || annotation.invisible ||
				this.props.pageNumber !== annotation.page || FunctionHelper.exists(annotation.markup?.relation) ||
				this.knownAnnotations.indexOf(annotation.subType as AnnotationSubType) < 0) {
				continue;
			}

			let page: PagePages | undefined = portalDocument.getPage(annotation.page);
			let positions: Array<Positions> = [];

			for (let position of FunctionHelper.applyValue(annotation.positions, Annotation.getPositionsDefault())) {
				// apply cropbox
				let tempPosition: Positions = position.clone();

				let positionPoint: AnnotationPoint = tempPosition as AnnotationPoint;
				if (FunctionHelper.exists(positionPoint.point)) {
					positionPoint.point!.x! -= FunctionHelper.applyValue(
						page?.boxes.cropBox?.x, MetadataRectangle.getXDefault()
					);
					positionPoint.point!.y! -= FunctionHelper.applyValue(
						page?.boxes.cropBox?.y, MetadataRectangle.getYDefault()
					);
				}

				let positionRectangle: MetadataAnnotationRectangle = tempPosition as MetadataAnnotationRectangle;
				if (FunctionHelper.exists(positionRectangle.rectangle)) {
					positionRectangle.rectangle!.x! -= FunctionHelper.applyValue(
						page?.boxes.cropBox?.x, MetadataRectangle.getXDefault()
					);
					positionRectangle.rectangle!.y! -= FunctionHelper.applyValue(
						page?.boxes.cropBox?.y, MetadataRectangle.getYDefault()
					);
				}

				positions.push(tempPosition);
			}

			annotations.push(AnnotationFactory.createInstance(Immutable.from({
				...Annotation.fromJson(annotation), positions: positions
			} as Annotation), {
				...this.props, key: annotations.length,
				zoomValue: UnitHelper.unitToPx(this.props.zoomValue, LengthUnit.POINTS, this.props.portalResolution)
			}));
		}

		return annotations;
	}

	private renderAnnotationMarker(): Array<ReactNode> {
		if (this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO) {
			return [];
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let annotationMarker: Array<ReactNode> = [];

		for (let baseAnnotation of FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		)) {
			let annotation: PortalAnnotation = PortalAnnotation.fromJson(baseAnnotation);
			if (this.props.pageNumber !== annotation.page || annotation.subType === AnnotationSubType.WIDGET) {
				continue;
			}

			let page: PagePages | undefined = portalDocument.getPage(annotation.page);
			let boundaries: MetadataRectangle = annotation!.getBoundaries(page);
			let selectorClass: Array<string> = ["annotation-selector"];

			if (annotation.objectKey === documentSettings.annotationSelection) {
				selectorClass.push("selected");
			}

			annotationMarker.push(
				<AnnotationRectangle {...this.props} {...boundaries} key={annotation.objectKey}
									 zoomValue={UnitHelper.unitToPx(
										 this.props.zoomValue, LengthUnit.POINTS, this.props.portalResolution
									 )}
									 className={selectorClass.join(" ")}
									 onClick={() => {
										 let portalDocument: PortalDocument =
											 this.props.context.documentManager.findDocument(
												 this.props.portalDocument.get().id!
											 );
										 let documentSettings: DocumentSettings = portalDocument.getSettings();
										 documentSettings.annotationSelection =
											 annotation.objectKey !== documentSettings.annotationSelection ?
												 FunctionHelper.applyValue(annotation.objectKey, null) :
												 null;
										 this.props.context.documentManager.updateGlobalDocumentList();
									 }} style={{cursor: "pointer"}}/>
			);
		}

		return annotationMarker;
	}

	private renderTextSearchMarker(): Array<ReactNode> {
		if (this.props.viewerMode !== ViewerMode.DEFAULT) {
			return [];
		}

		let searchMarker: Array<ReactNode> = []

		for (let textSearchResult of FunctionHelper.applyValue(this.props.textSearchResults?.get(), [])) {
			if (textSearchResult.data.page !== this.props.pageNumber) {
				continue;
			}

			let boundaries: MetadataRectangle = this.getTextBoundaries(textSearchResult);

			let searchMarkerClass = "search-marker";
			if (textSearchResult.key === this.props.textSearchSelection) {
				searchMarkerClass += " selected"
			}

			searchMarker.push(
				<AnnotationRectangle {...this.props} {...boundaries} key={searchMarker.length}
									 className={searchMarkerClass}
									 onClick={() => {
										 this.props.context.getPage().setState({
											 textSearchSelection: FunctionHelper.applyValue(
												 textSearchResult.key, ""
											 )
										 });
									 }}/>
			);
		}

		return searchMarker;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let annotationLayerClasses: Array<string> = ["annotation-layer"];

		if (this.props.mouseMode === MouseMode.MOVE && (
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO
		)) {
			annotationLayerClasses.push("popups");
		}

		if (this.props.mouseMode === MouseMode.TEXT && (
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO
		)) {
			annotationLayerClasses.push("selectable");
		}

		return (
			<div className={annotationLayerClasses.join(" ")} ref={this.annotationLayer}>
				<div className="generated" ref={this.annotationLayerGenerated}/>
				{this.renderAnnotations()}
				{this.renderAnnotationMarker()}
				{this.renderTextSearchMarker()}
			</div>
		);
	}
}

const AnnotationLayer = React.memo(AnnotationLayerDefinition,
	(prevProps: Readonly<AnnotationLayerProperties>, nextProps: Readonly<AnnotationLayerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.viewerMode !== prevProps.viewerMode) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.currentPage !== prevProps.currentPage) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.numberOfPages !== prevProps.numberOfPages) {
			return false;
		}

		return true;
	}
);

export default AnnotationLayer;